import React, { useState, useEffect } from 'react';
import { FileText, CheckCircle, XCircle, Clock, Eye, Building2, Mail, Globe, Calendar, Edit3 } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import { toast } from 'sonner';

const MainAdminApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedApp, setSelectedApp] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [customSubdomain, setCustomSubdomain] = useState('');
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);
  const [subdomainAvailable, setSubdomainAvailable] = useState(null);

  useEffect(() => {
    fetchApplications();
  }, [statusFilter]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await api.get(`/main-admin/applications${params}`);
      setApplications(response.data);
    } catch (error) {
      toast.error('Failed to fetch applications');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const checkSubdomain = async (subdomain) => {
    if (!subdomain || subdomain.length < 2) {
      setSubdomainAvailable(null);
      return;
    }
    setCheckingSubdomain(true);
    try {
      const response = await api.get(`/companies/check-subdomain/${subdomain.toLowerCase()}`);
      setSubdomainAvailable(response.data.available);
    } catch (error) {
      setSubdomainAvailable(null);
    } finally {
      setCheckingSubdomain(false);
    }
  };

  const openApproveModal = (app) => {
    setSelectedApp(app);
    setCustomSubdomain(app.subdomain);
    setSubdomainAvailable(null);
    checkSubdomain(app.subdomain);
    setShowApproveModal(true);
  };

  const handleApprove = async () => {
    if (!selectedApp) return;
    
    const subdomain = customSubdomain.toLowerCase().trim();
    if (!subdomain) {
      toast.error('Subdomain is required');
      return;
    }
    
    try {
      const response = await api.post(
        `/main-admin/applications/${selectedApp.id}/approve?custom_subdomain=${encodeURIComponent(subdomain)}`
      );
      toast.success(`Application approved! Login URL: ${response.data.login_url}`);
      setShowApproveModal(false);
      setSelectedApp(null);
      setCustomSubdomain('');
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve application');
    }
  };

  const handleReject = async () => {
    if (!selectedApp) return;
    try {
      await api.post(`/main-admin/applications/${selectedApp.id}/reject?notes=${encodeURIComponent(rejectNotes)}`);
      toast.success('Application rejected');
      setShowRejectModal(false);
      setSelectedApp(null);
      setRejectNotes('');
      fetchApplications();
    } catch (error) {
      toast.error('Failed to reject application');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
      approved: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle }
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        <Icon className="w-3 h-3" />
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  return (
    <Sidebar>
      <div className="p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Company Applications</h1>
            <p className="text-gray-600 mt-1">Review and process new company applications</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div 
              onClick={() => setStatusFilter('pending')}
              className={`cursor-pointer bg-white rounded-xl p-4 border-2 transition ${statusFilter === 'pending' ? 'border-yellow-500' : 'border-gray-200 hover:border-gray-300'}`}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {applications.filter(a => a.status === 'pending').length || '0'}
                  </p>
                  <p className="text-sm text-gray-500">Pending Review</p>
                </div>
              </div>
            </div>
            <div 
              onClick={() => setStatusFilter('approved')}
              className={`cursor-pointer bg-white rounded-xl p-4 border-2 transition ${statusFilter === 'approved' ? 'border-green-500' : 'border-gray-200 hover:border-gray-300'}`}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {applications.filter(a => a.status === 'approved').length || '0'}
                  </p>
                  <p className="text-sm text-gray-500">Approved</p>
                </div>
              </div>
            </div>
            <div 
              onClick={() => setStatusFilter('rejected')}
              className={`cursor-pointer bg-white rounded-xl p-4 border-2 transition ${statusFilter === 'rejected' ? 'border-red-500' : 'border-gray-200 hover:border-gray-300'}`}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {applications.filter(a => a.status === 'rejected').length || '0'}
                  </p>
                  <p className="text-sm text-gray-500">Rejected</p>
                </div>
              </div>
            </div>
          </div>

          {/* Applications List */}
          <div className="space-y-4">
            {loading ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
              </div>
            ) : applications.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No applications found</p>
              </div>
            ) : (
              applications.map((app) => (
                <div key={app.id} className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    {/* Company Info */}
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                        {app.logo_url ? (
                          <img src={app.logo_url} alt="" className="w-full h-full object-cover rounded-xl" />
                        ) : (
                          <Building2 className="w-8 h-8 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-semibold text-gray-900">{app.company_name}</h3>
                          {getStatusBadge(app.status)}
                        </div>
                        <div className="space-y-1 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4" />
                            <code className="px-2 py-0.5 bg-gray-100 rounded">{app.subdomain}.trados.app</code>
                          </div>
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            <span>{app.contact_email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>Applied {new Date(app.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        {app.description && (
                          <p className="mt-2 text-sm text-gray-600 line-clamp-2">{app.description}</p>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    {app.status === 'pending' && (
                      <div className="flex items-center gap-2 lg:flex-shrink-0">
                        <button
                          onClick={() => openApproveModal(app)}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Approve
                        </button>
                        <button
                          onClick={() => { setSelectedApp(app); setShowRejectModal(true); }}
                          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                        >
                          <XCircle className="w-4 h-4" />
                          Reject
                        </button>
                      </div>
                    )}

                    {app.status === 'approved' && app.company_id && (
                      <a
                        href={`/admin/companies/${app.company_id}`}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                      >
                        <Eye className="w-4 h-4" />
                        View Company
                      </a>
                    )}

                    {app.status === 'rejected' && app.admin_notes && (
                      <div className="lg:max-w-xs">
                        <p className="text-sm text-gray-500">Rejection reason:</p>
                        <p className="text-sm text-red-600">{app.admin_notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Approve Modal */}
      {showApproveModal && selectedApp && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Approve Application</h3>
            <p className="text-gray-600 mb-4">
              Approve <strong>{selectedApp.company_name}</strong> and create their company account.
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subdomain
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  value={customSubdomain}
                  onChange={(e) => {
                    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                    setCustomSubdomain(value);
                    checkSubdomain(value);
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-green-500"
                  placeholder="company-name"
                />
                <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg text-gray-500 text-sm">
                  .trados.app
                </span>
              </div>
              <div className="mt-1 flex items-center gap-2">
                {checkingSubdomain && (
                  <span className="text-sm text-gray-500">Checking availability...</span>
                )}
                {!checkingSubdomain && subdomainAvailable === true && (
                  <span className="text-sm text-green-600 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" /> Available
                  </span>
                )}
                {!checkingSubdomain && subdomainAvailable === false && (
                  <span className="text-sm text-red-600 flex items-center gap-1">
                    <XCircle className="w-4 h-4" /> Already taken
                  </span>
                )}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Login URL: https://{customSubdomain || 'subdomain'}.trados.app/login
              </p>
            </div>

            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => { setShowApproveModal(false); setSelectedApp(null); setCustomSubdomain(''); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={!customSubdomain || subdomainAvailable === false}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Approve & Create Company
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedApp && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reject Application</h3>
            <p className="text-gray-600 mb-4">
              You are about to reject the application from <strong>{selectedApp.company_name}</strong>.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rejection Reason (Optional)
              </label>
              <textarea
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                placeholder="Provide a reason for rejection..."
              />
            </div>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => { setShowRejectModal(false); setSelectedApp(null); setRejectNotes(''); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                Reject Application
              </button>
            </div>
          </div>
        </div>
      )}
    </Sidebar>
  );
};

export default MainAdminApplications;
